import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { POLLING_INTERVALS, createSmartInterval } from "../utils/polling";
import { chatApi, appTicketsApi } from "../services/api";

const Navdata = () => {
    const history = useNavigate();
    const { user, token, loading } = useAuth();

    // State for admin submenus
    const [isUsers, setIsUsers] = useState<boolean>(false);
    const [isFinance, setIsFinance] = useState<boolean>(false);
    const [isSystem, setIsSystem] = useState<boolean>(false);

    const [iscurrentState, setIscurrentState] = useState('Dashboard');

    // Chat unread count for badge
    const [chatUnreadCount, setChatUnreadCount] = useState<number>(0);

    // Ticket unread count for badge (regular users)
    const [ticketUnreadCount, setTicketUnreadCount] = useState<number>(0);

    // Admin ticket unread count for badge
    const [adminTicketUnreadCount, setAdminTicketUnreadCount] = useState<number>(0);

    // Notification sound for ticket updates
    const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
    const previousTicketUnreadRef = useRef<number>(-1); // -1 means first load (user)
    const previousAdminTicketUnreadRef = useRef<number>(-1); // -1 means first load (admin)

    // Initialize notification sound
    useEffect(() => {
        notificationSoundRef.current = new Audio('/sounds/notification.mp3');
    }, []);

    // Fetch chat unread count
    const fetchChatUnreadCount = useCallback(async () => {
        if (!user?.is_admin) return;

        try {
            const data = await chatApi.getRooms();

            if (data.success && data.rooms) {
                const totalUnread = data.rooms.reduce((sum: number, room: any) => {
                    return sum + (room.unreadCount || 0);
                }, 0);
                setChatUnreadCount(totalUnread);
            }
        } catch (error) {
            console.error('Error fetching chat unread count:', error);
        }
    }, [user?.is_admin]);

    // Fetch ticket unread count (for regular users)
    const fetchTicketUnreadCount = useCallback(async () => {
        // Skip if user not loaded, no token, or is admin
        if (!user || !token || user.is_admin) return;

        try {
            const data = await appTicketsApi.getUnreadCount();

            if (typeof data.unread_count === 'number') {
                const newCount = data.unread_count;

                // Play notification sound if unread count increased (skip first load)
                if (newCount > previousTicketUnreadRef.current && previousTicketUnreadRef.current >= 0) {
                    notificationSoundRef.current?.play().catch(() => {});
                }

                previousTicketUnreadRef.current = newCount;
                setTicketUnreadCount(newCount);
            }
        } catch (error) {
            console.error('Error fetching ticket unread count:', error);
        }
    }, [user, token]);

    // Poll for chat unread count (admin only)
    useEffect(() => {
        if (user?.is_admin) {
            fetchChatUnreadCount();
            const cleanup = createSmartInterval(
                fetchChatUnreadCount,
                POLLING_INTERVALS.CHAT_ROOMS_BADGE,
                { runImmediately: false, runOnVisible: true }
            );
            return () => cleanup();
        }
    }, [user?.is_admin, fetchChatUnreadCount]);

    // Poll for ticket unread count (regular users only)
    useEffect(() => {
        // Only fetch when user is loaded, has token, AND user is not admin
        if (user && token && !user.is_admin) {
            fetchTicketUnreadCount();
            const cleanup = createSmartInterval(
                fetchTicketUnreadCount,
                POLLING_INTERVALS.TICKETS_UNREAD,
                { runImmediately: false, runOnVisible: true }
            );

            // Listen for ticket viewed event to refresh immediately
            const handleTicketViewed = () => {
                fetchTicketUnreadCount();
            };
            window.addEventListener('ticketViewed', handleTicketViewed);

            return () => {
                cleanup();
                window.removeEventListener('ticketViewed', handleTicketViewed);
            };
        }
    }, [user, token, fetchTicketUnreadCount]);

    // Fetch admin ticket unread count
    const fetchAdminTicketUnreadCount = useCallback(async () => {
        // Skip if user not loaded, no token, or is not admin
        if (!user || !token || !user.is_admin) return;

        try {
            const data = await appTicketsApi.getAdminUnreadCount();

            if (typeof data.unread_count === 'number') {
                const newCount = data.unread_count;

                // Play notification sound if unread count increased (skip first load)
                if (newCount > previousAdminTicketUnreadRef.current && previousAdminTicketUnreadRef.current >= 0) {
                    notificationSoundRef.current?.play().catch(() => {});
                }

                previousAdminTicketUnreadRef.current = newCount;
                setAdminTicketUnreadCount(newCount);
            }
        } catch (error) {
            console.error('Error fetching admin ticket unread count:', error);
        }
    }, [user, token]);

    // Poll for admin ticket unread count (admin only)
    useEffect(() => {
        if (user && token && user.is_admin) {
            fetchAdminTicketUnreadCount();
            const cleanup = createSmartInterval(
                fetchAdminTicketUnreadCount,
                POLLING_INTERVALS.TICKETS_UNREAD,
                { runImmediately: false, runOnVisible: true }
            );

            // Listen for admin ticket viewed event to refresh immediately
            const handleAdminTicketViewed = () => {
                fetchAdminTicketUnreadCount();
            };
            window.addEventListener('adminTicketViewed', handleAdminTicketViewed);

            return () => {
                cleanup();
                window.removeEventListener('adminTicketViewed', handleAdminTicketViewed);
            };
        }
    }, [user, token, fetchAdminTicketUnreadCount]);

    // useEffect must be called before any conditional returns (React hooks rule)
    useEffect(() => {
        document.body.classList.remove('twocolumn-panel');
    }, [history, iscurrentState]);

    // Return empty while loading to prevent flash
    if (loading || !user) {
        return <React.Fragment>{[]}</React.Fragment>;
    }

    function updateIconSidebar(e: any) {
        if (e && e.target && e.target.getAttribute("sub-items")) {
            const ul: any = document.getElementById("two-column-menu");
            const iconItems: any = ul.querySelectorAll(".nav-icon.active");
            let activeIconItems = [...iconItems];
            activeIconItems.forEach((item) => {
                item.classList.remove("active");
                var id = item.getAttribute("sub-items");
                const getID = document.getElementById(id) as HTMLElement;
                if (getID)
                    getID.classList.remove("show");
            });
        }
    }

    // Check if user is admin
    const isAdmin = user?.is_admin || user?.role === 'admin';

    // ADMIN MENUS
    const adminMenuItems: any = [
        {
            label: "Admin Panel",
            isHeader: true,
        },
        {
            id: "admin-dashboard",
            label: "Dashboard",
            icon: "ri-dashboard-3-line",
            link: "/admin/dashboard",
            click: function (e: any) {
                e.preventDefault();
                history("/admin/dashboard");
            },
        },
        {
            label: "User Management",
            isHeader: true,
        },
        {
            id: "users",
            label: "Users",
            icon: "ri-team-line",
            link: "/#",
            click: function (e: any) {
                e.preventDefault();
                setIsUsers(!isUsers);
                setIscurrentState('Users');
                updateIconSidebar(e);
            },
            stateVariables: isUsers,
            subItems: [
                {
                    id: "user-list",
                    label: "User List",
                    link: "/admin/users",
                    parentId: "users",
                    icon: "ri-list-check"
                },
                {
                    id: "store-management",
                    label: "Store Management",
                    link: "/admin/stores",
                    parentId: "users",
                    icon: "ri-store-2-line"
                },
                {
                    id: "ebay-accounts",
                    label: "eBay Accounts",
                    link: "/admin/ebay-accounts",
                    parentId: "users",
                    icon: "ri-shopping-cart-2-line"
                },
            ]
        },
        {
            label: "Finance & Billing",
            isHeader: true,
        },
        {
            id: "finance",
            label: "Finance",
            icon: "ri-money-pound-circle-line",
            link: "/#",
            click: function (e: any) {
                e.preventDefault();
                setIsFinance(!isFinance);
                setIscurrentState('Finance');
                updateIconSidebar(e);
            },
            stateVariables: isFinance,
            subItems: [
                {
                    id: "payments",
                    label: "Payments",
                    link: "/admin/payments",
                    parentId: "finance",
                    icon: "ri-bank-card-line"
                },
                {
                    id: "invoices",
                    label: "Invoices",
                    link: "/admin/invoices",
                    parentId: "finance",
                    icon: "ri-file-list-3-line"
                },
                {
                    id: "credits",
                    label: "Credits",
                    link: "/admin/credits",
                    parentId: "finance",
                    icon: "ri-coin-line"
                },
                {
                    id: "subscription-health",
                    label: "Subscription Health",
                    link: "/admin/subscription-health",
                    parentId: "finance",
                    icon: "ri-heart-pulse-line"
                },
                {
                    id: "discount-codes",
                    label: "Discount Codes",
                    link: "/admin/discount-codes",
                    parentId: "finance",
                    icon: "ri-coupon-3-line"
                },
            ]
        },
        {
            label: "Communications",
            isHeader: true,
        },
        {
            id: "notifications",
            label: "Announcements",
            icon: "ri-notification-3-line",
            link: "/admin/notifications",
            click: function (e: any) {
                e.preventDefault();
                history("/admin/notifications");
            },
        },
        {
            id: "tickets",
            label: "Support Tickets",
            icon: "ri-customer-service-2-line",
            link: "/apps-tickets-list",
            badgeName: adminTicketUnreadCount > 0 ? adminTicketUnreadCount.toString() : null,
            badgeColor: "danger",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-tickets-list");
            },
        },
        {
            id: "chat",
            label: "Chat",
            icon: "ri-chat-3-line",
            link: "/apps-chat",
            badgeName: chatUnreadCount > 0 ? chatUnreadCount.toString() : null,
            badgeColor: "danger",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-chat");
            },
        },
        {
            id: "mailbox",
            label: "Mailbox",
            icon: "ri-mail-line",
            link: "/apps-mailbox",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-mailbox");
            },
        },
        {
            label: "Analytics & Reports",
            isHeader: true,
        },
        {
            id: "reports",
            label: "Reports",
            icon: "ri-line-chart-line",
            link: "/admin/reports",
            click: function (e: any) {
                e.preventDefault();
                history("/admin/reports");
            },
        },
        {
            id: "audit-logs",
            label: "Audit Logs",
            icon: "ri-shield-check-line",
            link: "/admin/audit-logs",
            click: function (e: any) {
                e.preventDefault();
                history("/admin/audit-logs");
            },
        },
        {
            id: "scraper-monitoring",
            label: "Scraper Monitor",
            icon: "ri-radar-line",
            link: "/admin/scraper-monitoring",
            click: function (e: any) {
                e.preventDefault();
                history("/admin/scraper-monitoring");
            },
        },
        {
            label: "System Settings",
            isHeader: true,
        },
        {
            id: "system",
            label: "System",
            icon: "ri-settings-3-line",
            link: "/#",
            click: function (e: any) {
                e.preventDefault();
                setIsSystem(!isSystem);
                setIscurrentState('System');
                updateIconSidebar(e);
            },
            stateVariables: isSystem,
            subItems: [
                {
                    id: "system-settings",
                    label: "System Settings",
                    link: "/admin/system-settings",
                    parentId: "system",
                    icon: "ri-settings-4-line"
                },
                {
                    id: "email-settings",
                    label: "Email Settings",
                    link: "/admin/email-settings",
                    parentId: "system",
                    icon: "ri-mail-settings-line"
                },
                {
                    id: "system-health",
                    label: "System Health",
                    link: "/admin/system-health",
                    parentId: "system",
                    icon: "ri-heart-pulse-line"
                },
                {
                    id: "ops-health",
                    label: "Ops Health",
                    link: "/admin/ops-health",
                    parentId: "system",
                    icon: "ri-pulse-line"
                },
            ]
        },
    ];

    // USER MENUS
    const userMenuItems: any = [
        {
            label: "Main Menu",
            isHeader: true,
        },
        {
            id: "dashboard",
            label: "Dashboard",
            icon: "ri-dashboard-2-line",
            link: "/dashboard",
            click: function (e: any) {
                e.preventDefault();
                history("/dashboard");
            },
        },
        {
            id: "products",
            label: "Products",
            icon: "ri-shopping-bag-line",
            link: "/apps-ecommerce-products",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-ecommerce-products");
            },
        },
        {
            id: "orders",
            label: "Orders",
            icon: "ri-file-list-3-line",
            link: "/apps-ecommerce-orders",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-ecommerce-orders");
            },
        },
        {
            id: "product-import",
            label: "Product Import",
            icon: "ri-download-cloud-line",
            link: "/apps-ecommerce-drafts",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-ecommerce-drafts");
            },
        },
        {
            label: "Settings",
            isHeader: true,
        },
        {
            id: "settings",
            label: "Settings",
            icon: "ri-settings-3-line",
            link: "/settings",
            click: function (e: any) {
                e.preventDefault();
                history("/settings");
            },
        },
        {
            id: "store-management",
            label: "Store Management",
            icon: "ri-store-2-line",
            link: "/settings/stores",
            click: function (e: any) {
                e.preventDefault();
                history("/settings/stores");
            },
        },
        {
            id: "subscription",
            label: "Subscription",
            icon: "ri-vip-crown-line",
            link: "/subscription",
            click: function (e: any) {
                e.preventDefault();
                history("/subscription");
            },
        },
        {
            label: "Support",
            isHeader: true,
        },
        {
            id: "support-tickets",
            label: "Support Tickets",
            icon: "ri-customer-service-2-line",
            link: "/apps-tickets-list",
            badgeName: ticketUnreadCount > 0 ? ticketUnreadCount.toString() : null,
            badgeColor: "danger",
            click: function (e: any) {
                e.preventDefault();
                history("/apps-tickets-list");
            },
        },
        {
            id: "help",
            label: "Help",
            icon: "mdi mdi-lifebuoy",
            link: "/pages-faqs",
            click: function (e: any) {
                e.preventDefault();
                history("/pages-faqs");
            },
        },
    ];

    // Return appropriate menu based on user role
    const menuItems = isAdmin ? adminMenuItems : userMenuItems;

    return <React.Fragment>{menuItems}</React.Fragment>;
};

export default Navdata;
